import { memo, type ReactNode } from 'react';
// ============================================================
// NarrativeEntryStructured - Collapsible combat log entry with
// detail sections (rolls, damage breakdown, defence chain, crits,
// weapon effects). Outcome-based border colors and effect tags.
//
// Uses native <details>/<summary> for accessible, zero-JS collapse.
// ============================================================
import common from '../styles/common.module.css';
import { damageLedgerRows } from '../engine/damageBreakdownRows';
import { magnitudeSummary, woundsSummary } from '../engine/statusLineText';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import type {
  StructuredNarrativeEntry,
  StructuredDetail,
  AttackOutcome,
  DamageBreakdown,
  StatusSummary,
} from '../engine/structuredNarrative';

const OUTCOME_COLORS: Record<AttackOutcome, string> = {
  hit: 'var(--ok)',
  partial: 'var(--warn)',
  deflected: 'var(--info)',
  blocked: 'var(--arcane)',
  miss: 'var(--neutral)',
  jam: 'var(--danger)',
};

interface Props {
  entry: StructuredNarrativeEntry;
}

function RollsSection({ rolls }: { rolls: NonNullable<StructuredDetail['rolls']> }) {
  return (
    <div style={{ marginTop: 4 }}>
      {rolls.map((r, i) => (
        <div key={i} style={{ fontSize: 10, color: r.success ? 'var(--ok-soft)' : 'var(--danger-soft)' }}>
          {r.label}: {r.roll} vs {r.target} {r.success ? '\u2714' : '\u2718'}
        </div>
      ))}
    </div>
  );
}

const rowStyle = {
  fontSize: 10,
  display: 'flex',
  justifyContent: 'space-between',
  maxWidth: 260,
  color: 'var(--neutral-soft)',
} as const;

function BreakdownRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={rowStyle}>
      <span>{label}</span>
      <span style={{ color: color || 'var(--neutral-bright)' }}>{value}</span>
    </div>
  );
}

/** A hit the force field stopped: the field's roll and whether it overloaded. */
function FieldBlockedHit({ d }: { d: DamageBreakdown }) {
  const threshold = d.ffOverloadThreshold ?? 0;
  return (
    <div style={{ fontSize: 10, marginBottom: 4 }}>
      <div style={{ color: 'var(--info)', fontWeight: 'bold' }}>
        Hit {d.hit}: {d.location} - FORCE FIELD BLOCKED
      </div>
      <div style={{ marginLeft: 12 }}>
        {d.ffRoll != null && (
          <BreakdownRow label="FF Roll" value={'d100=' + d.ffRoll + ' vs ' + (d.ffProtection ?? '?')} />
        )}
        {d.ffOverloaded ? (
          <BreakdownRow
            label="Overload"
            value={'FIELD OVERLOADED! (rolled \u2264' + (d.ffOverloadThreshold ?? '?') + ')'}
            color="var(--warn)"
          />
        ) : threshold > 0 ? (
          <BreakdownRow
            label="Overload"
            value={'No overload (threshold \u2264' + threshold + ')'}
            color="var(--text-dim)"
          />
        ) : null}
      </div>
    </div>
  );
}

function EnrichedHit({ d }: { d: DamageBreakdown }) {
  if (d.ffBlocked) return <FieldBlockedHit d={d} />;
  return (
    <div style={{ fontSize: 10, marginBottom: 4 }}>
      <div style={{ color: d.effective > 0 ? 'var(--neutral-bright)' : 'var(--neutral)', fontWeight: 'bold' }}>
        Hit {d.hit}: {d.location} - {d.effective > 0 ? d.effective + ' wounds' : 'absorbed'}
        {d.damageType && <span style={{ color: 'var(--text-dim)', fontWeight: 'normal' }}> [{d.damageType}]</span>}
      </div>
      <div style={{ marginLeft: 12 }}>
        {damageLedgerRows(d).map((row) => (
          <BreakdownRow key={row.label} label={row.label} value={row.value} color={row.color} />
        ))}
      </div>
    </div>
  );
}

function legacyHitColor(d: DamageBreakdown): string {
  if (d.ffBlocked) return 'var(--info)';
  return d.effective > 0 ? 'var(--neutral-bright)' : 'var(--neutral)';
}

/** One line per hit, for entries logged before the ledger was enriched. */
function LegacyHit({ d }: { d: DamageBreakdown }) {
  return (
    <div style={{ fontSize: 10, color: legacyHitColor(d) }}>
      {d.ffBlocked
        ? d.location + ': FORCE FIELD blocked'
        : d.location + ': ' + d.raw + ' raw - ' + d.ap + ' AP - ' + d.tb + ' TB = ' + d.effective + ' dmg'}
    </div>
  );
}

/** Enrichment data is present: a dice string or a force-field roll. */
const isEnriched = (d: DamageBreakdown) => d.diceStr != null || (d.ffRoll != null && d.ffRoll > 0);

function DamageSection({ breakdown }: { breakdown: NonNullable<StructuredDetail['damageBreakdown']> }) {
  // Feature detection: the enriched view if any hit carries enrichment,
  // else the simple legacy view (an entry logged before it existed).
  const Hit = breakdown.some(isEnriched) ? EnrichedHit : LegacyHit;
  return (
    <div style={{ marginTop: 4 }}>
      {breakdown.map((d, i) => (
        <Hit key={i} d={d} />
      ))}
    </div>
  );
}

function DefenceSection({ steps }: { steps: NonNullable<StructuredDetail['defenceDetails']> }) {
  return (
    <div style={{ marginTop: 4 }}>
      {steps.map((s, i) => (
        <div key={i} style={{ fontSize: 10, color: s.success ? 'var(--ok-soft)' : 'var(--danger-soft)' }}>
          {s.label}: {s.roll} vs {s.skill}
          {s.success ? ' \u2714 avoided ' + s.avoided : ' \u2718 failed'}
          {s.counterAttackTriggered && <span style={{ color: 'var(--warn)' }}> [Counter-Attack!]</span>}
        </div>
      ))}
    </div>
  );
}

function CritSection({ crits }: { crits: NonNullable<StructuredDetail['criticalEffects']> }) {
  return (
    <div style={{ marginTop: 4 }}>
      {crits.map((c, i) => (
        <div key={i} style={{ fontSize: 10, color: 'var(--danger)' }}>
          {c.location} Sev {c.severity}: {c.text}
        </div>
      ))}
    </div>
  );
}

function WeaponEffectsSection({ effects }: { effects: NonNullable<StructuredDetail['weaponEffects']> }) {
  return (
    <div style={{ marginTop: 4 }}>
      {effects.map((e, i) => (
        <div key={i} style={{ fontSize: 10, color: e.success === false ? 'var(--warn-soft)' : 'var(--neutral-soft)' }}>
          {e.icon} {e.result}
        </div>
      ))}
    </div>
  );
}

function EffectTags({ tags }: { tags: string[] }) {
  return (
    <span style={{ marginLeft: 6 }}>
      {tags.map((tag, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            fontSize: 9,
            padding: '1px 4px',
            marginLeft: 3,
            borderRadius: 3,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
          }}
        >
          {tag}
        </span>
      ))}
    </span>
  );
}

function HordeStatus({ status, showNumbers }: { status: StatusSummary; showNumbers: boolean }) {
  if (!status.magDmg) return null;
  const tally = { dealt: status.magDmg, after: status.magAfter, max: status.magMax, destroyed: !!status.isDestroyed };
  return (
    <div
      style={{
        marginTop: 6,
        fontWeight: 'bold',
        fontSize: 10,
        color: status.isDestroyed ? 'var(--danger-strong)' : 'var(--warn)',
      }}
    >
      {magnitudeSummary(tally, showNumbers)}
    </div>
  );
}

function woundsText(status: StatusSummary, showNumbers: boolean): string | null {
  if (status.isDead) return '\uD83D\uDC80 TARGET IS DEAD!';
  if (!(status.totalDamage > 0)) return null;
  return woundsSummary({ dealt: status.totalDamage, after: status.woundsAfter, max: status.woundsMax }, showNumbers);
}

function StatusLine({ status, showNumbers }: { status: StatusSummary; showNumbers: boolean }) {
  if (status.isHorde) return <HordeStatus status={status} showNumbers={showNumbers} />;
  if (status.totalDamage === 0 && !status.isDead) return null;
  const wColor = status.isDead || status.woundsAfter < 0 ? 'var(--danger-strong)' : 'var(--danger)';
  return (
    <div style={{ marginTop: 6, fontWeight: 'bold', fontSize: 10, color: wColor }}>
      {woundsText(status, showNumbers)}
    </div>
  );
}

function nonEmpty<T>(list: T[] | undefined): list is T[] {
  return !!list && list.length > 0;
}

/** Whether a status line would say anything: damage taken, a death, a destroyed horde. */
function statusWorthShowing(status: StatusSummary | undefined): boolean {
  return !!status && (status.totalDamage > 0 || !!status.isDead || !!status.isDestroyed);
}

/** Anything to fold out below the summary; without it the entry is a plain line. */
function hasDetail(detail: StructuredDetail): boolean {
  const lists = [
    detail.rolls,
    detail.damageBreakdown,
    detail.defenceDetails,
    detail.criticalEffects,
    detail.weaponEffects,
    detail.specialNotes,
  ];
  return lists.some((list) => nonEmpty<unknown>(list)) || statusWorthShowing(detail.status);
}

/** A titled fold inside the entry: Rolls, Defence Chain, Damage Breakdown... */
function Fold({ title, color, open, children }: { title: string; color: string; open?: boolean; children: ReactNode }) {
  return (
    <details style={{ marginLeft: 8, marginTop: 4 }} open={open}>
      <summary style={{ fontSize: 9, color, cursor: 'pointer' }}>{title}</summary>
      {children}
    </details>
  );
}

function SpecialNotesSection({ notes }: { notes: string[] }) {
  return (
    <div style={{ marginLeft: 12, marginTop: 4 }}>
      {notes.map((n, i) => (
        <div key={i} style={{ fontSize: 10, color: 'var(--arcane)' }}>
          {n}
        </div>
      ))}
    </div>
  );
}

const modifierRowStyle = {
  fontSize: 10,
  color: 'var(--neutral-bright)',
  display: 'flex',
  justifyContent: 'space-between',
  maxWidth: 220,
} as const;

function ModifiersSection({ modifiers }: { modifiers: NonNullable<StructuredDetail['modifiers']> }) {
  return (
    <div style={{ marginTop: 4 }}>
      {modifiers.map((m, i) => (
        <div key={i} style={modifierRowStyle}>
          <span>{m.label}</span>
          <span style={{ color: m.value >= 0 ? 'var(--ok-soft)' : 'var(--danger-soft)' }}>
            {m.value >= 0 ? '+' : ''}
            {m.value}
          </span>
        </div>
      ))}
    </div>
  );
}

const FOLD_BLUE = 'var(--info-soft)';

/** The folds under the summary, each only when it has something to show. */
function DetailFolds({ detail, showNumbers }: { detail: StructuredDetail; showNumbers: boolean }) {
  return (
    <>
      {nonEmpty(detail.specialNotes) && <SpecialNotesSection notes={detail.specialNotes} />}
      {nonEmpty(detail.modifiers) && (
        <Fold title="Modifiers" color={FOLD_BLUE}>
          <ModifiersSection modifiers={detail.modifiers} />
        </Fold>
      )}
      {nonEmpty(detail.rolls) && (
        <Fold title="Rolls" color={FOLD_BLUE} open>
          <RollsSection rolls={detail.rolls} />
        </Fold>
      )}
      {nonEmpty(detail.defenceDetails) && (
        <Fold title="Defence Chain" color={FOLD_BLUE} open>
          <DefenceSection steps={detail.defenceDetails} />
        </Fold>
      )}
      {/* OMITTED WHOLESALE rather than redacted row by row. The breakdown
          is twenty numbers that reconstruct the wound total exactly -
          dice, armour, Toughness, effective damage - and a redaction that
          has to remember all of them is one someone will later add a row
          to and forget. The summary line still says how bad it was. */}
      {showNumbers && nonEmpty(detail.damageBreakdown) && (
        <Fold title="Damage Breakdown" color={FOLD_BLUE} open>
          <DamageSection breakdown={detail.damageBreakdown} />
        </Fold>
      )}
      {nonEmpty(detail.criticalEffects) && (
        <Fold title="Critical Effects" color="var(--danger)" open>
          <CritSection crits={detail.criticalEffects} />
        </Fold>
      )}
      {nonEmpty(detail.weaponEffects) && (
        <Fold title="Weapon Effects" color="var(--warn-soft)" open>
          <WeaponEffectsSection effects={detail.weaponEffects} />
        </Fold>
      )}
      {detail.status && <StatusLine showNumbers={showNumbers} status={detail.status} />}
    </>
  );
}

function NarrativeEntryStructured({ entry }: Props) {
  const { summary, detail, outcome, effectTags } = entry;
  const { showNumbers } = useWoundDisplay();
  const style = { borderLeft: '3px solid ' + OUTCOME_COLORS[outcome || 'hit'] };
  const tags = nonEmpty(effectTags) && <EffectTags tags={effectTags} />;

  if (!hasDetail(detail)) {
    return (
      <div className={common.logEntry} style={style}>
        {summary}
        {tags}
      </div>
    );
  }

  return (
    <details className={common.logEntry} style={style}>
      <summary style={{ cursor: 'pointer', listStyle: 'disclosure-closed' }}>
        {summary}
        {tags}
      </summary>
      <DetailFolds detail={detail} showNumbers={showNumbers} />
    </details>
  );
}

export default memo(NarrativeEntryStructured);
